import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Download, Pencil, Plus, ShieldCheck, Search } from "lucide-react";
import { CONSENT_TONE, G, download, errMsg } from "./guestApi";
import { GuestFormDialog } from "./GuestFormDialog";
import { ConsentDialog } from "./ConsentDialog";

export function GuestList({ session, center, refreshKey }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [consent, setConsent] = useState("ALL");
  const [page, setPage] = useState(1);
  const [form, setForm] = useState({ open: false, contact: null });
  const [consentFor, setConsentFor] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.post(`${G}/list`, { token: session.token, center, q,
        consent: consent === "ALL" ? null : consent, page, page_size: 50 });
      setRows(r.data.rows); setTotal(r.data.total);
    } catch (e) { toast.error(errMsg(e)); }
  }, [session.token, center, q, consent, page]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const exportXlsx = () => download(`${G}/export`, { token: session.token, center, q,
    consent: consent === "ALL" ? null : consent }, `guest_contacts_${center}.xlsx`)
    .catch((e) => toast.error(errMsg(e)));

  return (
    <div className="space-y-3" data-testid="guest-list">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-2 top-2.5 text-slate-400" />
          <Input className="pl-8 w-64" placeholder="Search name, phone, email" value={q}
            onChange={(e) => { setQ(e.target.value); setPage(1); }} data-testid="guest-search" />
        </div>
        <Select value={consent} onValueChange={(v) => { setConsent(v); setPage(1); }}>
          <SelectTrigger className="w-44" data-testid="guest-consent-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All consent</SelectItem>
            <SelectItem value="OPTED_IN">Opted in</SelectItem>
            <SelectItem value="UNKNOWN">Unknown</SelectItem>
            <SelectItem value="OPTED_OUT">Opted out</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-xs text-slate-500" data-testid="guest-total">{total} guests</span>
        <div className="ml-auto flex gap-2">
          <Button size="sm" variant="outline" onClick={exportXlsx} data-testid="guest-export-btn">
            <Download className="w-4 h-4 mr-1" />Export Excel</Button>
          <Button size="sm" onClick={() => setForm({ open: true, contact: null })}
            data-testid="guest-add-btn"><Plus className="w-4 h-4 mr-1" />Add guest</Button>
        </div>
      </div>
      <div className="border rounded overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
            <tr><th className="p-2">Guest</th><th className="p-2">Phone</th>
              <th className="p-2">Email</th><th className="p-2">City</th>
              <th className="p-2">Consent</th><th className="p-2">Source</th>
              <th className="p-2 w-40"></th></tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={7} className="p-6 text-center text-slate-500" data-testid="guest-empty">
                No guests yet for {center}. Add one, paste a list or import Excel.</td></tr>)}
            {rows.map((r) => (
              <tr key={r.id} className="border-t hover:bg-slate-50" data-testid={`guest-row-${r.id}`}>
                <td className="p-2 font-medium">{r.name || <span className="text-slate-400">—</span>}</td>
                <td className="p-2 font-mono text-xs">{r.phone_e164}
                  {r.alt_phones?.length > 0 && <span className="text-slate-400"> +{r.alt_phones.length}</span>}</td>
                <td className="p-2 text-xs">{r.email}</td>
                <td className="p-2 text-xs">{r.city}</td>
                <td className="p-2"><Badge className={CONSENT_TONE[r.consent_status]}
                  data-testid={`guest-consent-${r.id}`}>{r.consent_status}</Badge></td>
                <td className="p-2 text-xs text-slate-500">{r.source}</td>
                <td className="p-2 flex gap-1">
                  <Button size="sm" variant="ghost" onClick={() => setForm({ open: true, contact: r })}
                    data-testid={`guest-edit-${r.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => setConsentFor(r)}
                    data-testid={`guest-consent-btn-${r.id}`}><ShieldCheck className="w-3.5 h-3.5 mr-1" />Consent</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {total > 50 && (
        <div className="flex gap-2 items-center text-xs">
          <Button size="sm" variant="outline" disabled={page === 1} onClick={() => setPage(page - 1)}
            data-testid="guest-prev-page">Prev</Button>
          Page {page} of {Math.ceil(total / 50)}
          <Button size="sm" variant="outline" disabled={page * 50 >= total} onClick={() => setPage(page + 1)}
            data-testid="guest-next-page">Next</Button>
        </div>)}
      <GuestFormDialog open={form.open} contact={form.contact} center={center} session={session}
        onClose={() => setForm({ open: false, contact: null })} onSaved={load} />
      <ConsentDialog open={!!consentFor} contact={consentFor} session={session}
        onClose={() => setConsentFor(null)} onSaved={load} />
    </div>
  );
}
